import { useEffect, useState, useRef, useContext, useCallback } from "react";

import { Notification } from "@rio-cloud/rio-uikit";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { IPackage } from "@types";

import CardCount from "src/components/CardCount";
import { useDebounce } from "src/hooks/useDebounce";
import { fetchPackagesPage, fetchPackagesSummary, confirmPackageDelivery, cancelPackageDelivery, markPackageNotReceived, fetchBatchLogsList, fetchBatchLog, IBatchUploadResult } from "src/services/packages";
import PageContainer from "../../components/routes/PageContainer";
import Package from "src/components/Page/Package";
import { ConfigPaginator } from "src/components/PaginatorSize";
import { AuthContext } from "src/providers/AuthProvider";

const PAGE_SIZE = 10;

type FilterType = "all" | "pending" | "received" | "cancelled" | "to_be_confirmed" | "delayed";

const PackagesScreen = () => {
  const { profileId } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState("desc");
  const [fieldSort, setFieldSort] = useState("createdAt");
  const [currentSize, setCurrentSize] = useState(PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const isInitialLoad = useRef(true);
  const [dashboard, setDashboard] = useState({
    pending: 0,
    received: 0,
    cancelled: 0,
    delayed: 0,
    toBeConfirmed: 0,
    total: 0,
  });
  const debouncedSearch = useDebounce(searchValue, 500);

  const [batchLog, setBatchLog] = useState<IBatchUploadResult | null>(null);
  const batchPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current);
      batchPollingRef.current = null;
    }
  }, []);

  const pollLogById = useCallback((logId: string) => {
    stopPolling();
    batchPollingRef.current = setInterval(async () => {
      try {
        const res = await fetchBatchLog(logId);
        const result = res.data.result;
        setBatchLog(result);

        if (result.status !== "PROCESSING") {
          stopPolling();
          if (result.status === "SUCCESS" || result.status === "PARTIAL") {
            loadPackagesPage(currentSize, true);
            loadSummary();
          }
        }
      } catch {
        stopPolling();
      }
    }, 15000);
  }, [stopPolling]);

  const loadLatestBatchLog = useCallback(async () => {
    try {
      const res = await fetchBatchLogsList(0, 1);
      const content = res.data.result.content;
      if (content && content.length > 0) {
        const latest = content[0];
        setBatchLog(latest);

        if (latest.status === "PROCESSING" && latest.id) {
          pollLogById(latest.id);
        }
      }
    } catch {
      // silently fail
    }
  }, [pollLogById]);

  useEffect(() => {
    loadSummary();
    loadPackagesPage(PAGE_SIZE, true);
    loadLatestBatchLog();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    // Resetar e carregar do início quando filtros mudam
    setCurrentSize(PAGE_SIZE);
    loadPackagesPage(PAGE_SIZE, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sort, fieldSort, activeFilter, startDate, endDate]);

  const loadSummary = async () => {
    try {
      const response = await fetchPackagesSummary();
      const summary = response.data.result;

      setDashboard({
        pending: summary.pending,
        received: summary.received,
        cancelled: summary.cancelled,
        delayed: summary.delayed,
        toBeConfirmed: summary.toBeConfirmed,
        total: summary.total,
      });
    } catch (error) {
      console.error("Erro ao buscar resumo dos pacotes:", error);
      Notification.error("Não foi possível buscar o resumo dos pacotes.");
    }
  };

  const loadPackagesPage = async (size: number, isReset: boolean = false) => {
    if (isReset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      // Converter filtro para formato do backend
      let statusFilter: string | undefined = undefined;
      if (activeFilter && activeFilter !== "all") {
        statusFilter = activeFilter;
      }

      const response = await fetchPackagesPage(
        0,
        size,
        debouncedSearch || undefined,
        sort || undefined,
        fieldSort || undefined,
        statusFilter,
        startDate,
        endDate
      );

      // Validar estrutura da resposta
      if (!response || !response.content) {
        throw new Error("Estrutura de resposta inválida");
      }

      setPackages(response.content);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error("Erro ao buscar pacotes:", error);
      Notification.error("Não foi possível buscar os pacotes.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = (config: ConfigPaginator) => {
    const newSize = config.next;
    setCurrentSize(newSize);
    loadPackagesPage(newSize, false);
  };

  const handleReset = () => {
    setCurrentSize(PAGE_SIZE);
    loadPackagesPage(PAGE_SIZE, true);
  };

  const handleDeletePackage = async (id: string) => {
    try {
      await cancelPackageDelivery(id, profileId);
      Notification.success("Pacote cancelado com sucesso!");
      loadPackagesPage(currentSize, false);
      loadSummary();
    } catch (error) {
      console.error("Erro ao cancelar pacote:", error);
      Notification.error("Não foi possível cancelar o pacote.");
    }
  };

  const handleCreatePackage = () => {
    // A lógica de criação será tratada pelo modal no componente Package
  };

  const handleUpdatePackage = (id: string) => {
    // TODO: Implementar navegação para edição de pacote
    Notification.info("Funcionalidade de edição será implementada em breve!");
  };

  const handleConfirmDelivery = async (id: string, status?: string) => {
    try {
      await confirmPackageDelivery(id, profileId);
      if (status === "PENDING") {
        Notification.success("Entrega confirmada com sucesso! Legal que recebeu seu pacote antes da previsão de entrega.");
      } else {
        Notification.success("Entrega confirmada com sucesso!");
      }
      loadPackagesPage(currentSize, false);
      loadSummary();
    } catch (error) {
      console.error("Erro ao confirmar entrega:", error);
      Notification.error("Não foi possível confirmar a entrega.");
    }
  };

  const handleMarkNotReceived = async (id: string) => {
    try {
      await markPackageNotReceived(id);
      Notification.success("Pacote marcado como não recebido!");
      loadPackagesPage(currentSize, false);
      loadSummary();
    } catch (error) {
      console.error("Erro ao marcar pacote como não recebido:", error);
      Notification.error("Não foi possível marcar o pacote como não recebido.");
    }
  };

  const handleDownload = () => { };

  const handleSearchValueChange = (text: string) => {
    setSearchValue(text);
  };

  const handleCardClick = (filter: FilterType) => {
    // Toggle: se clicar no mesmo card, desativa o filtro
    if (activeFilter === filter) {
      setActiveFilter(null);
    } else {
      setActiveFilter(filter);
    }
  };

  const handleDateFilterChange = (start?: string, end?: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleResetDate = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  return (
    <PageContainer>
      <div>
 <div className="display-flex gap-20">
          <CardCount
            borderColor="#30B4C0"
            label="EM ROTA"
            value={dashboard.pending}
            onClick={() => handleCardClick("pending")}
            isActive={activeFilter === "pending"}
          />

          <CardCount
            borderColor="#F5A623"
            label="A CONFIRMAR"
            value={dashboard.toBeConfirmed}
            onClick={() => handleCardClick("to_be_confirmed")}
            isActive={activeFilter === "to_be_confirmed"}
          />

         <CardCount
           borderColor="#43BE2B"
           label="ENTREGA CONFIRMADA"
           value={dashboard.received}
           onClick={() => handleCardClick("received")}
           isActive={activeFilter === "received"}
         />

          <CardCount
            borderColor="#DB2B2B"
            label="ATRASADOS"
            value={dashboard.delayed}
            onClick={() => handleCardClick("delayed")}
            isActive={activeFilter === "delayed"}
          />

          <CardCount
            borderColor="#DB2B2B"
            label="CANCELADOS"
            value={dashboard.cancelled}
            onClick={() => handleCardClick("cancelled")}
            isActive={activeFilter === "cancelled"}
          />

          <CardCount
            borderColor="#868C93"
            label="TOTAL DE PACOTES"
            value={dashboard.total}
            onClick={() => handleCardClick("all")}
            isActive={activeFilter === "all"}
          />
        </div>
      </div>

      {/* Status do último processamento em lote */}
      {batchLog && (() => {
        const isProcessing = batchLog.status === "PROCESSING";
        const isSuccess = batchLog.status === "SUCCESS";
        const isPartial = batchLog.status === "PARTIAL";
        const isError = batchLog.status === "ERROR";
        const textColor = isSuccess ? "#30B4C0" : (isError || isPartial) ? "#D90000" : "#2A3740";
        const statusText = isProcessing
          ? `${batchLog.progress ?? 0}%`
          : isSuccess
            ? "Concluído"
            : isPartial
              ? "Erro na criação"
              : isError
                ? "Erro na criação"
                : `${batchLog.progress ?? 0}%`;

        return (
          <span
            style={{
              fontFamily: 'Source Sans Pro, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 18,
              lineHeight: '100%',
              letterSpacing: '0%',
              marginTop: 15,
              display: 'block',
            }}
          >
            Arquivos (CONEMB e NOTFIS) em processamento:{" "}
            <span style={{ color: textColor, fontFamily: 'Source Sans Pro, sans-serif', fontWeight: 600, fontSize: 18, lineHeight: '100%' }}>{statusText}</span>
          </span>
        );
      })()}

      <Package
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        showHeader
        totalElements={totalElements}
        handleDownload={handleDownload}
        handleSort={(dir, field) => {
          setSort(dir);
          setFieldSort(field);
        }}
        packages={packages}
        viewType="TABLE"
        handleCreatePackage={handleCreatePackage}
        handleUpdatePackage={handleUpdatePackage}
        handleConfirmDelivery={handleConfirmDelivery}
        handleMarkNotReceived={handleMarkNotReceived}
        handleSearchValueChange={handleSearchValueChange}
        handleDeletePackage={handleDeletePackage}
        searchValue={searchValue}
        handleLoadMore={handleLoadMore}
        handleReset={handleReset}
        onPackageCreated={() => {
          loadPackagesPage(currentSize, true);
          loadSummary();
          loadLatestBatchLog();
        }}
        onDateFilterChange={handleDateFilterChange}
        handleResetDate={handleResetDate}
      />
    </PageContainer>
  );
};

export default PackagesScreen;
